import { Link, useLocation } from 'react-router';
import { ChevronRightIcon } from 'lucide-react';

import { TRAILS } from '@/app/nav';
import { useT } from '@/lib/i18n';

/**
 * The trail above a page title, and now an actual trail (`plan-beta` E).
 *
 * ### What was wrong
 *
 * The crumb text existed and did nothing. "Assessment Design · Questions" sat
 * above the item bank looking exactly like a breadcrumb and behaving like a
 * label — which is worse than no breadcrumb, because a reader learns once that
 * this console's crumbs are decorative and stops trying.
 *
 * ### Where the trail comes from
 *
 * `TRAILS` in `nav.ts`, beside the navigation it mirrors. A trail is a property
 * of a ROUTE, so deriving it here from `useLocation` means no page passes one and
 * none can pass a wrong one — the failure mode of the previous version, where
 * each screen hand-typed its own crumb and two of them disagreed with the nav.
 *
 * **A segment without an `href` is not a link**, and that is deliberate rather
 * than an omission: "Assessment Design" is a nav grouping with no screen behind
 * it, and a crumb that navigated somewhere arbitrary to seem clickable is the
 * decorative version again, wearing a pointer cursor.
 */
export function Breadcrumbs({ fallback }: { fallback?: string }) {
  const t = useT();
  const { pathname } = useLocation();
  const trail = TRAILS[pathname];

  if (!trail) {
    // A route with no trail keeps whatever the page passed — the harness and the
    // result view have bespoke eyebrows that are not navigation.
    return fallback ? <Crumb>{fallback}</Crumb> : null;
  }

  return (
    <nav aria-label={t('chrome.breadcrumb')} className="mb-1.5 flex items-center gap-1">
      {trail.map((segment, i) => (
        <span key={segment.title} className="flex items-center gap-1">
          {i > 0 && (
            <ChevronRightIcon
              aria-hidden
              className="text-muted-foreground/60 size-3 rtl:rotate-180"
            />
          )}
          {segment.href ? (
            <Link
              to={segment.href}
              className="text-muted-foreground hover:text-foreground rounded-sm text-xs font-semibold tracking-[0.06em] uppercase transition-colors"
            >
              {t(segment.title)}
            </Link>
          ) : (
            <Crumb>{t(segment.title)}</Crumb>
          )}
        </span>
      ))}
    </nav>
  );
}

const Crumb = ({ children }: { children: React.ReactNode }) => (
  <span className="text-muted-foreground text-xs font-semibold tracking-[0.06em] uppercase">
    {children}
  </span>
);
